import * as React from "react";
import { useEffect, useState } from "react";
import { api } from "../../../utils/axios";
import "../styles/adminDashboard.css";
import "../styles/applicationErrors.css";
import { useNavigate } from "react-router-dom";

interface ErrorRecord {
  id: number;
  service_name: string;
  error_type: string;
  error_subtype: string;
  search_value: string;
  error_message: string;
  user_session_id: string;
  created_at: string;
  environment: string;
  has_context_data: boolean;
  resolved: boolean;
}

interface Pagination {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

interface Summary {
  period: { start_date: string; end_date: string; days: number };
  service_error_counts: any[];
  daily_trends: any[];
  top_problematic_searches: any[];
  service_statistics: any[];
}

const ApplicationErrors: React.FC = () => {
  // Filters
  const [serviceName, setServiceName] = useState("");
  const [errorType, setErrorType] = useState("");
  const [errorSubtype, setErrorSubtype] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tags, setTags] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [resolvedFilter, setResolvedFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  
  // Applied filters (only these trigger API calls)
  const [appliedSearchValue, setAppliedSearchValue] = useState("");
  const [appliedErrorSubtype, setAppliedErrorSubtype] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [appliedTags, setAppliedTags] = useState("");
  
  // Monthly summary toggle
  const [showMonthlySummary, setShowMonthlySummary] = useState(false);

  // Data
  const [errors, setErrors] = useState<ErrorRecord[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [services, setServices] = useState<{ name: string; error_count: number }[]>([]);
  const [errorTypes, setErrorTypes] = useState<{ name: string; error_count: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  // CSV Export handler
  const handleExportCSV = () => {
    if (errors.length === 0) {
      alert("No data to export");
      return;
    }

    const fields = [
      "id",
      "service_name", 
      "error_type",
      "error_subtype",
      "search_value",
      "error_message",
      "user_session_id",
      "created_at",
      "environment",
      "resolved",
      "tags",
      "http_status_code",
      "request_url",
      "request_method",
      "client_ip",
      "user_identity",
      "log_level"
    ];

    const headers = [
      "ID",
      "Service Name",
      "Error Type", 
      "Error Subtype",
      "Search Value",
      "Error Message",
      "User Session ID",
      "Created At",
      "Environment",
      "Resolved",
      "Tags",
      "HTTP Status Code",
      "Request URL",
      "Request Method",
      "Client IP",
      "User Identity",
      "Log Level"
    ];

    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...errors.map(error => 
        fields.map(field => {
          let value = (error as any)[field];
          
          // Handle null/undefined values
          if (value === null || value === undefined) {
            value = "";
          }
          // Handle boolean values
          else if (typeof value === "boolean") {
            value = value ? "Yes" : "No";
          }
          // Handle strings with commas or quotes
          else if (typeof value === "string") {
            if (value.includes(",") || value.includes('"') || value.includes('\n')) {
              value = `"${value.replace(/"/g, '""')}"`;
            }
          }
          // Convert other types to string
          else {
            value = String(value);
          }
          
          return value;
        }).join(",")
      )
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    
    // Generate filename with current filters and timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const filterSuffix = [serviceName, errorType, errorSubtype, searchValue, startDate, endDate]
      .filter(Boolean).length > 0 ? "_filtered" : "_all";
    link.download = `application_errors${filterSuffix}_${timestamp}.csv`;
    
    link.click();
    URL.revokeObjectURL(url);
  };

  // Refresh handler
  const handleRefresh = () => {
    setLoading(true);
    setError(null);
    // Re-fetch filters
    api.get("/admin/application-errors/services")
      .then((res) => {
        setServices(res.data.services);
        setErrorTypes(res.data.error_types);
      })
      .catch(() => setError("Unable to load filter options."));
    // Re-fetch summary
    api.get("/admin/application-errors/summary")
      .then((res) => setSummary(res.data))
      .catch(() => setError("Unable to load summary."));
    // Re-fetch errors
    api
      .get("/admin/application-errors", {
        params: {
          page,
          page_size: pageSize,
          service_name: serviceName || undefined,
          error_type: errorType || undefined,
          error_subtype: appliedErrorSubtype || undefined,
          search_value: appliedSearchValue || undefined,
          start_date: appliedStartDate || undefined,
          end_date: appliedEndDate || undefined,
          tags: appliedTags || undefined,
          resolved: resolvedFilter !== '' ? resolvedFilter : undefined,
        },
      })
      .then((res) => {
        setErrors(res.data.errors);
        setPagination(res.data.pagination);
      })
      .catch(() => setError("Unable to load application errors."))
      .finally(() => setLoading(false));
  };

  const handleApplyFilters = () => {
    setAppliedSearchValue(searchValue);
    setAppliedErrorSubtype(errorSubtype);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedTags(tags);
    setPage(1); // Reset to first page when applying new filters
  };

  const hasActiveFilters = () => {
    return serviceName || errorType || appliedErrorSubtype || appliedSearchValue || 
           appliedTags || appliedStartDate || appliedEndDate || resolvedFilter;
  };

  const clearAllFilters = () => {
    setServiceName("");
    setErrorType("");
    setErrorSubtype("");
    setSearchValue("");
    setTags("");
    setStartDate("");
    setEndDate("");
    setResolvedFilter("");
    // Clear applied filters too
    setAppliedSearchValue("");
    setAppliedErrorSubtype("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setAppliedTags("");
    setPage(1);
  };

  const handleSetResolved = async (id: number, resolved: boolean) => {
    await api.post(`/admin/application-errors/${id}/resolve`, { resolved });
    handleRefresh();
  };

  const handleBulkSetResolved = async (resolved: boolean) => {
    if (selectedIds.length === 0) return;
    await api.post('/admin/application-errors/bulk-resolve', { ids: selectedIds, resolved });
    setSelectedIds([]);
    handleRefresh();
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMode, setDeleteMode] = useState<'single' | 'bulk'>('single');
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openDeleteModal = (mode: 'single' | 'bulk', id?: number) => {
    setDeleteMode(mode);
    setDeleteTargetId(id ?? null);
    setIsDeleting(false); // Reset deleting state when opening modal
    setShowDeleteModal(true);
  };
  const closeDeleteModal = () => {
    setIsDeleting(false); // Reset deleting state when closing modal
    setShowDeleteModal(false);
  };
  
  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      if (deleteMode === 'bulk') {
        await api.post('/admin/application-errors/bulk-delete', { ids: selectedIds });
        setSelectedIds([]);
      } else if (deleteTargetId !== null) {
        await api.delete(`/admin/application-errors/${deleteTargetId}`);
      }
      setShowDeleteModal(false);
      handleRefresh();
    } catch (error) {
      console.error('Delete failed:', error);
      setError('Failed to delete error(s). Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(errors.map(e => e.id));
    } else {
      setSelectedIds([]);
    }
  };
  const handleSelectRow = (id: number, checked: boolean) => {
    setSelectedIds(prev => checked ? [...prev, id] : prev.filter(i => i !== id));
  };

  // Fetch dropdowns
  useEffect(() => {
    api.get("/admin/application-errors/services")
      .then((res) => {
        setServices(res.data.services);
        setErrorTypes(res.data.error_types);
        setError(null);
      })
      .catch(() => setError("Unable to load filter options."));
  }, []);

  // Fetch summary
  useEffect(() => {
    api.get("/admin/application-errors/summary")
      .then((res) => {
        setSummary(res.data);
        setError(null);
      })
      .catch(() => setError("Unable to load summary."));
  }, []);

  // Fetch errors (only trigger on applied filters, not pending input)
  useEffect(() => {
    setLoading(true);
    api
      .get("/admin/application-errors", {
        params: {
          page,
          page_size: pageSize,
          service_name: serviceName || undefined,
          error_type: errorType || undefined,
          error_subtype: appliedErrorSubtype || undefined,
          search_value: appliedSearchValue || undefined,
          start_date: appliedStartDate || undefined,
          end_date: appliedEndDate || undefined,
          tags: appliedTags || undefined,
          resolved: resolvedFilter !== '' ? resolvedFilter : undefined,
        },
      })
      .then((res) => {
        setErrors(res.data.errors);
        setPagination(res.data.pagination);
        setError(null);
      })
      .catch(() => setError("Unable to load application errors."))
      .finally(() => setLoading(false));
  }, [page, pageSize, serviceName, errorType, appliedErrorSubtype, appliedSearchValue, appliedStartDate, appliedEndDate, appliedTags, resolvedFilter]);

  return (
    <div className="admin-dashboard-wrapper">
      <header className="admin-header-bar">
        <div className="admin-header-logo">
          <div>
            <h1>Admin Portal</h1>
            <p>Manage client operations, uploads, and validations</p>
          </div>
        </div>
      </header>
      <div className="admin-dashboard-container">
        <button
          className="back-to-dashboard-btn"
          onClick={() => navigate('/admin-dashboard')}
        >
          ← Back to Dashboard
        </button>
        <h2>Application Errors</h2>
        <div className="admin-filters-section">
          <select value={serviceName} onChange={e => setServiceName(e.target.value)}>
            <option value="">All Services</option>
            {services.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
          </select>
          <select value={errorType} onChange={e => setErrorType(e.target.value)}>
            <option value="">All Error Types</option>
            {errorTypes.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
          </select>
          <input type="text" placeholder="Error Subtype" value={errorSubtype} onChange={e => setErrorSubtype(e.target.value)} />
          <input type="text" placeholder="Search Value" value={searchValue} onChange={e => setSearchValue(e.target.value)} />
          <input type="text" placeholder="Tags" value={tags} onChange={e => setTags(e.target.value)} />
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          <select value={resolvedFilter} onChange={e => setResolvedFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="true">Resolved</option>
            <option value="false">Unresolved</option>
          </select>
          <div className="mobile-button-grid">
            <button onClick={handleApplyFilters}>Apply Filters</button>
          <button className="refresh-btn" onClick={handleRefresh} disabled={loading} title="Refresh">
            <span style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }}>
              &#x21bb;
            </span>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
            <button 
              className="export-csv-btn" 
              onClick={handleExportCSV} 
              disabled={loading || errors.length === 0}
              title="Export displayed results as CSV"
            >
              Export CSV
            </button>
          </div>
        </div>
        {error && (
          <div className="admin-error-message">{error}</div>
        )}

        {/* Monthly Summary - Collapsible Section */}
        {summary && !error && (
          <div className="summary-toggle-section">
            <button 
              className="summary-toggle-btn"
              onClick={() => setShowMonthlySummary(!showMonthlySummary)}
            >
              <span className="toggle-icon">{showMonthlySummary ? '−' : '+'}</span>
              {" "}
              Monthly Summary 
              <span className="summary-quick-info">
                ({summary.service_error_counts.reduce((acc, s) => acc + s.count, 0)} errors in last 30 days)
              </span>
            </button>
            
            {showMonthlySummary && (
          <div className="admin-summary-section">
                <div className="summary-period">
              <b>Period:</b> {summary.period.start_date.slice(0, 10)} to {summary.period.end_date.slice(0, 10)} ({summary.period.days} days)
            </div>
                <div className="summary-total">
                  <b>Total Errors in Period:</b> {summary.service_error_counts.reduce((acc, s) => acc + s.count, 0)}
                </div>

                {/* Top Problematic Searches */}
                {summary.top_problematic_searches && summary.top_problematic_searches.length > 0 && (
                  <div className="summary-breakdown">
                    <h4>Top Problematic Searches:</h4>
                    <div className="problematic-searches">
                      {summary.top_problematic_searches.slice(0, 5).map((search, idx) => (
                        <div key={idx} className="problematic-search">
                          <div className="search-info">
                            <span className="search-service">{search.service_name}</span>
                            <span className="search-type">{search.error_subtype}</span>
                            <span className="search-value">"{search.search_value}"</span>
                          </div>
                          <span className="search-count">{search.count} errors</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Service Statistics */}
                {summary.service_statistics && summary.service_statistics.length > 0 && (
                  <div className="summary-breakdown">
                    <h4>Service Details:</h4>
                    <div className="service-details">
                      {summary.service_statistics.map((stat, idx) => (
                        <div key={idx} className="service-detail">
                          <div className="service-detail-header">
                            <span className="service-detail-name">{stat.service_name}</span>
                            <span className="service-detail-total">{stat.total_errors} errors</span>
                          </div>
                          <div className="service-detail-info">
                            <span className="detail-item">
                              <strong>{stat.unique_error_types}</strong> error types
                            </span>
                            {stat.first_error && (
                              <span className="detail-item">
                                First: {new Date(stat.first_error).toLocaleDateString()}
                              </span>
                            )}
                            {stat.last_error && (
                              <span className="detail-item">
                                Latest: {new Date(stat.last_error).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Daily Trends */}
                {summary.daily_trends && summary.daily_trends.length > 0 && (
                  <div className="summary-breakdown">
                    <h4>Recent Daily Activity:</h4>
                    <div className="daily-trends">
                      {summary.daily_trends.slice(-7).map((trend, idx) => (
                        <div key={idx} className="daily-trend">
                          <span className="trend-date">{new Date(trend.date).toLocaleDateString()}</span>
                          <span className="trend-service">{trend.service_name}</span>
                          <span className="trend-type">{trend.error_type}</span>
                          <span className="trend-count">{trend.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="summary-actions">
                  <button 
                    className="view-full-month-btn"
                    onClick={() => {
                      // Clear current filters and set date range to show full month
                      setServiceName("");
                      setErrorType("");
                      setErrorSubtype("");
                      setSearchValue("");
                      setTags("");
                      setResolvedFilter("");
                      const startDate = summary.period.start_date.slice(0, 10);
                      const endDate = summary.period.end_date.slice(0, 10);
                      setStartDate(startDate);
                      setEndDate(endDate);
                      setAppliedSearchValue("");
                      setAppliedErrorSubtype("");
                      setAppliedTags("");
                      setAppliedStartDate(startDate);
                      setAppliedEndDate(endDate);
                      setPage(1);
                      setShowMonthlySummary(false);
                    }}
                  >
                    View All Errors from This Period
                  </button>
                </div>
                
                <div className="summary-note">
                  <em>Note: This summary shows all errors for the last 30 days, regardless of current filters.</em>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Data Overview */}
        {!loading && !error && (
          <div className="data-overview-section">
            <div className="overview-stats">
              <span className="overview-showing">
                <strong>{errors.length}</strong> {errors.length === 1 ? 'record' : 'records'}
                {pagination && pagination.total_count !== errors.length && (
                  <span className="overview-total">of {pagination.total_count} matching</span>
                )}
              </span>
              {hasActiveFilters() && (
                <span className="overview-filters">
                  {[serviceName, errorType, appliedErrorSubtype, appliedSearchValue, appliedTags, appliedStartDate, appliedEndDate, resolvedFilter].filter(Boolean).length} filter{[serviceName, errorType, appliedErrorSubtype, appliedSearchValue, appliedTags, appliedStartDate, appliedEndDate, resolvedFilter].filter(Boolean).length !== 1 ? 's' : ''} active
                </span>
              )}
            </div>
          </div>
        )}

        {selectedIds.length > 0 && (
          <div className="bulk-actions">
            <button className="bulk-resolve-btn" onClick={() => handleBulkSetResolved(true)}>Mark as Resolved</button>
            <button className="bulk-unresolve-btn" onClick={() => handleBulkSetResolved(false)}>Mark as Unresolved</button>
            <button className="bulk-delete-btn" onClick={() => openDeleteModal('bulk')}>Delete Selected</button>
            <span style={{ marginLeft: 12, color: '#555' }}>{selectedIds.length} selected</span>
          </div>
        )}
        <div className="admin-errors-table-section">
          <div className="admin-errors-table-scroll">
            {loading ? (
              <div className="no-results-message">Loading...</div>
            ) : error ? null : errors.length === 0 ? (
              <div className="no-results-message">
                {hasActiveFilters() ? (
                  <div>
                    <h3>No results found</h3>
                    <p>No errors match your current filter criteria:</p>
                    <ul className="filter-summary">
                      {serviceName && <li>Service: <strong>{serviceName}</strong></li>}
                      {errorType && <li>Error Type: <strong>{errorType}</strong></li>}
                      {appliedErrorSubtype && <li>Error Subtype: <strong>{appliedErrorSubtype}</strong></li>}
                      {appliedSearchValue && <li>Search Value: <strong>{appliedSearchValue}</strong></li>}
                      {appliedTags && <li>Tags: <strong>{appliedTags}</strong></li>}
                      {appliedStartDate && <li>Start Date: <strong>{appliedStartDate}</strong></li>}
                      {appliedEndDate && <li>End Date: <strong>{appliedEndDate}</strong></li>}
                      {resolvedFilter && <li>Status: <strong>{resolvedFilter === 'true' ? 'Resolved' : 'Unresolved'}</strong></li>}
                    </ul>
                    <p>Try adjusting your filters or <button onClick={clearAllFilters} className="clear-filters-btn">clear all filters</button> to see more results.</p>
                  </div>
                ) : (
                  <div>
                    <h3>No errors found</h3>
                    <p>Great news! There are currently no application errors in the system.</p>
                  </div>
                )}
              </div>
            ) : (
              <table className="admin-errors-table">
                <thead>
                  <tr>
                    <th><input type="checkbox" checked={selectedIds.length === errors.length && errors.length > 0} onChange={handleSelectAll} /></th>
                    <th>ID</th>
                    <th>Service</th>
                    <th>Error Type</th>
                    <th>Subtype</th>
                    <th>Search Value</th>
                    <th>Message</th>
                    <th>Date</th>
                    <th>Env</th>
                    <th>Resolved</th>
                    <th>Delete</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map(error => (
                    <tr key={error.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(error.id)}
                          onChange={e => handleSelectRow(error.id, e.target.checked)}
                        />
                      </td>
                      <td>{error.id}</td>
                      <td>{error.service_name}</td>
                      <td>{error.error_type}</td>
                      <td>{error.error_subtype}</td>
                      <td>{error.search_value}</td>
                      <td>{error.error_message?.slice(0, 40)}</td>
                      <td>{error.created_at.slice(0, 19).replace('T', ' ')}</td>
                      <td>{error.environment}</td>
                      <td className="actions-cell">
                        <span className={error.resolved ? "resolved-badge" : "unresolved-badge"}>
                          {error.resolved ? "Resolved" : "Unresolved"}
                        </span>
                        <button
                          className={`resolve-toggle-btn ${error.resolved ? 'unresolve' : ''}`}
                          onClick={() => handleSetResolved(error.id, !error.resolved)}
                        >
                          {error.resolved ? "Unresolve" : "Resolve"}
                        </button>
                      </td>
                      <td className="actions-cell">
                        <button
                          className="delete-btn"
                          onClick={() => openDeleteModal('single', error.id)}
                        >
                          Delete
                        </button>
                      </td>
                      <td>
                        <button
                          className="view-btn"
                          onClick={() => navigate(`/admin/application-errors/${error.id}`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {/* Pagination just below the table, not sticky/fixed */}
          {pagination && !error && (
            <div className="admin-pagination">
              <button disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
              <span>Page {page} of {pagination.total_pages}</span>
              <button disabled={page === pagination.total_pages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          )}
        </div>
      </div>
      {showDeleteModal && (
        <div className="admin-error-modal-backdrop">
          <div className="admin-error-modal-dialog">
            <h3>Confirm Delete</h3>
            <p>
              {deleteMode === 'bulk'
                ? `Are you sure you want to delete ${selectedIds.length} errors?`
                : 'Are you sure you want to delete this error?'}
            </p>

            <button 
              className="delete-confirm-btn" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                confirmDelete();
              }}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closeDeleteModal();
              }}
              disabled={isDeleting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationErrors;
